import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Options } from '@ascenda/ngx-slider';
import { RTL_LOCALE_CODES } from '@rd-lib/rtl-locales';
import { LocaleFacade } from '@rd/core/facades/locale-facade/locale.facade';
import { CashPointsSliderConfig, Locale } from '@rd/types';
import { roundNumber } from '@rd/utils/round-number.utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export const defaultCashPointsSliderConfig: CashPointsSliderConfig = {
  defaultStep: 0,
  sliderCashLabel: 'cash_points_slider.label.cash_only',
  sliderLabelPosition: 'top',
  sliderMaxValue: 1,
  sliderMinValue: 0,
  sliderPointsLabel: 'cash_points_slider.label.points_only',
  sliderStepValue: 0.05,
};

@Component({
  selector: 'rd-shared-cash-points-slider',
  templateUrl: './cash-points-slider.component.html',
  styleUrls: ['./cash-points-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CashPointsSliderComponent implements OnInit, OnChanges, OnDestroy {
  @HostBinding('class.rd-shared-cash-points-slider') componentClass = true;
  @Input() value: number;
  @Input() sliderConfig: CashPointsSliderConfig = defaultCashPointsSliderConfig;
  @Output() sliderValueChange: EventEmitter<{
    value: number;
  }> = new EventEmitter();

  ngxSliderOptions: Options;
  private destroyed$ = new Subject();

  constructor(
    private cdRef: ChangeDetectorRef,
    private localeFacade: LocaleFacade,
  ) {}

  ngOnInit(): void {
    this.ngxSliderOptions = {
      floor: this.sliderConfig.sliderMinValue,
      ceil: this.sliderConfig.sliderMaxValue,
      value: this.value || this.sliderConfig.defaultStep,
      step: this.sliderConfig.sliderStepValue,
      labelPosition: 'bottom',
      valueLabel: this.formatSliderValue,
    };

    this.localeFacade.selectedLocale$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((locale) => {
        // Ascenda Slider needs to remember the slider value because when locale is changed,
        // we have to update the config / options, which then overwrite the slider value with the default one.
        // Hence, we must not leave the value prop as undefined from the beginning.
        this.ngxSliderOptions = {
          ...this.ngxSliderOptions,
          value: this.value,
          // @ts-ignore
          isRtl: this.isRTLLocale(locale.code),
        };
        this.cdRef.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // This allows the consumer to manually change the value
    if (changes.value && changes.value.currentValue) {
      this.ngxSliderOptions = {
        ...this.ngxSliderOptions,
        value: changes.value.currentValue,
      };
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  formatSliderValue(value: number): string {
    return `${roundNumber(value * 100, 0)}%`;
  }

  onInputChange(sliderValue: number): void {
    this.sliderValueChange.emit({
      value: sliderValue,
    });
  }

  private isRTLLocale(localeCode: Locale['code']): boolean {
    return RTL_LOCALE_CODES.includes(localeCode);
  }
}
